import React, { useState } from 'react';
import { useTranslation } from 'services/localization/useTranslation';
import DiscountCodesTable from 'components/DiscountCodesTable';
import * as ShopSelectors from 'selectors/ShopSelectors';
import { useSelector } from 'react-redux';
import SearchBar from 'components/SearchBar';
import { Box } from '@mui/material';
import { Routes } from 'routing';
import LinkButton from 'common/components/LinkButton';
import SetPageTitle from 'components/Portals/SetPageTitle';
import HasFeature from 'components/features/HasFeature';
import AddOnPromoPage from 'components/features/promos/AddOnPromoPage';
import { Redirect } from 'react-router-dom';
import EmptyState from 'common/components/states/EmptyState';

const Discounts = () => {
	const { t } = useTranslation();
	const discountCodes = useSelector(ShopSelectors.discountCodesData);
	const loading = useSelector(ShopSelectors.discountCodesLoading);
	const [searchValue, setSearchValue] = useState<string>('');

	const trimmedSearchValue = searchValue.toLowerCase().trim();

	const filteredDiscountCodes = discountCodes.filter((code) => {
		return code.code.toLowerCase().trim().indexOf(trimmedSearchValue) !== -1;
	});

	const hasDiscountCodes = discountCodes.length > 0;

	const createDiscountCodeButton = (
		<LinkButton to={Routes.CATALOG_CREATE_DISCOUNT} variant="contained" color="primary">
			{t('discount.newDiscountCode', 'New discount code')}
		</LinkButton>
	);

	return (
		<>
			<SetPageTitle
				title={t('products.catalog', 'Catalog')}
				priority={2}
				styling={{ fluid: true }}
				rightContent={
					hasDiscountCodes ? (
						<HasFeature requiredFeature="DISCOUNTS">{createDiscountCodeButton}</HasFeature>
					) : undefined
				}
			/>
			<HasFeature
				requiredFeature="DISCOUNTS"
				fallback={(failedChecks) => {
					if (failedChecks.user) return <Redirect to={Routes.CATALOG} />;
					return <AddOnPromoPage addOn="DISCOUNT_CODES" />;
				}}
			>
				{hasDiscountCodes ? (
					<Box>
						<SearchBar
							defaultValue={searchValue}
							onChange={setSearchValue}
							placeholder={t('discount.searchForACode', 'Search for a code')}
						/>
						<DiscountCodesTable
							discountCodes={filteredDiscountCodes}
							searchValue={searchValue}
							loading={loading}
						/>
					</Box>
				) : (
					<EmptyState
						cta={createDiscountCodeButton}
						title={t('DiscountCodesTable.noDiscountCodes', 'You have no discount codes')}
						description={t(
							'DiscountCodesTable.noDiscountCodesDescription',
							`Once you've added some discount codes, you'll be able to manage them here.`,
						)}
					/>
				)}
			</HasFeature>
		</>
	);
};

export default Discounts;
